// `sso-required-email-validator.directive.ts` + the `ng-model-options` the two templates
// put on their email field, ported as a state machine and a hook over it.
//
// Upstream writes this as an `$asyncValidators.ssoRequired` on the email input, with
//
//     ng-model-options="{ updateOn: 'default blur', debounce: { default: 400, blur: 0 } }"
//
// on `login.page.tpl.html:22` and `sign-up.page.tpl.html`. Four behaviours come out of that
// pair, and every one of them was added upstream in response to something going wrong:
//
//   - **debounced 400ms.** The validator fires on every model update, and without the
//     debounce that is one request per keystroke — a probe per character of an email
//     address, against an endpoint that asks the identity provider.
//   - **flushed on blur.** `updateOn: 'default blur'` with `debounce.blur: 0` is what makes
//     leaving the field commit immediately; without the `blur` trigger registered, tabbing
//     to the password field and pressing Enter submits while the probe for the address you
//     just typed has not been sent.
//   - **fails open.** A failed probe is not evidence of an SSO requirement. Blocking on it
//     locks ordinary users out of a tenant that never required SSO, which is much the worse
//     failure.
//   - **gates the submit.** While the probe is in flight the form must not go through, or a
//     user who types and hits Enter signs in with a password on an SSO-only account.
//
// ⚠️ THE MACHINE IS SEPARATE FROM THE HOOK BECAUSE THE BEHAVIOUR IS THE TIMING, and this
// repo's tests are pure — there is no DOM renderer in the suite and adding one to test a
// debounce would be a large dependency for a small function. `createSsoRequiredProbe` owns
// the timer, the staleness guard and the fail-open branch; `useSsoRequiredProbe` is the
// React wrapper and holds no logic of its own.
//
// ⚠️ WHAT IS DELIBERATELY NOT PORTED: `suppressExceptions`. Upstream passes it so
// AngularJS's `HttpErrorInterceptor` does not pop a generic error alert for the non-2xx
// before the catch runs — which would defeat failing open by putting a modal on screen.
// `packages/api` has no interceptor and no global alert, so there is nothing to suppress;
// the rejection reaches the catch below and nothing else sees it.
import { useEffect, useMemo, useState } from 'react'
import { ApiError, ResponseBodyError } from '@woo-ext/api'

/**
 * `describeProbeFailure` (whitelabel-ext `sso-required-email-validator.component.ts`): the status
 * and content-type the failed probe came back with, so a tenant whose probe is answered by a
 * proxy's HTML page is diagnosable from the log. Undefined for both when nothing answered.
 */
export function describeProbeFailure(error: unknown): { status?: number; contentType?: string | null } {
  if (error instanceof ResponseBodyError) return { status: error.status, contentType: error.contentType }
  // `ApiError` keeps the status but not the headers — the non-2xx case predates this log line.
  if (error instanceof ApiError) return { status: error.status }
  return {}
}

/** `debounce: { default: 400, blur: 0 }`. */
export const SSO_PROBE_DEBOUNCE_MS = 400

export interface SsoProbeState {
  /** The server said this address must use SSO. Only ever set from an explicit `true`. */
  ssoRequired: boolean
  /** A probe is scheduled or in flight. The submit is disabled while it is. */
  pending: boolean
}

export interface SsoProbeOptions {
  /** Absent on a form that does not probe — the machine is then inert and never pends. */
  check?: (email: string) => Promise<boolean>
  /**
   * `attrs.required`: with it, an empty value still probes; without it, upstream returns
   * early and the field stays valid. Both templates mark the field required.
   */
  required?: boolean
  /**
   * Default `SSO_PROBE_DEBOUNCE_MS`. 0 for a field with no `ng-model-options` debounce, where
   * the validator runs on every change (the popup's forgot-password email).
   */
  debounceMs?: number
  /** What the fail-open log line names. The machine also runs the popup's `isValidEmail`. */
  label?: string
  onState: (state: SsoProbeState) => void
}

export interface SsoProbeMachine {
  /** Every keystroke: schedules a probe 400ms after typing stops. */
  change: (email: string) => void
  /** Blur: flushes the pending probe immediately, as `debounce.blur: 0` does. */
  blur: (email: string) => void
  /** Drops the pending timer and ignores any answer still in flight. */
  dispose: () => void
}

export function createSsoRequiredProbe({
  check,
  required = true,
  debounceMs = SSO_PROBE_DEBOUNCE_MS,
  label = 'isSsoRequired',
  onState,
}: SsoProbeOptions): SsoProbeMachine {
  let timer: ReturnType<typeof setTimeout> | undefined
  // ⚠️ THE LAST VALUE PROBED, NOT A COUNTER. Answers arrive out of order — a slow probe for
  // `a@b.c` can land after a fast one for the address the user has since typed — and
  // applying a stale one is how a corrected address keeps showing the SSO error.
  let latest = ''
  let disposed = false

  const run = async (email: string): Promise<void> => {
    if (check === undefined) return
    try {
      const answer = await check(email)
      if (disposed || latest !== email) return
      onState({ ssoRequired: answer, pending: false })
    } catch (error) {
      // Fail open — see the header. A probe that could not answer says nothing about SSO.
      //
      // Logged, as upstream's `$log.error('isSsoRequired probe failed, treating as
      // not-SSO-required', describeProbeFailure(e))` is: failing open is silent by design,
      // so without this a tenant whose probe is broken looks exactly like one that does not
      // use SSO, and nobody finds out until an SSO user signs in with a password.
      console.error(`${label} probe failed, failing open`, describeProbeFailure(error), error)
      if (!disposed && latest === email) onState({ ssoRequired: false, pending: false })
    }
  }

  const schedule = (email: string, delay: number): void => {
    if (disposed) return
    if (timer !== undefined) clearTimeout(timer)
    latest = email
    // Typing invalidates the previous answer at once: leaving it up while a new probe runs
    // shows an error about an address that is no longer in the field.
    if (check === undefined || (!required && email.length === 0)) {
      onState({ ssoRequired: false, pending: false })
      return
    }
    onState({ ssoRequired: false, pending: true })
    timer = setTimeout(() => {
      void run(email)
    }, delay)
  }

  return {
    change: (email) => {
      schedule(email, debounceMs)
    },
    blur: (email) => {
      schedule(email, 0)
    },
    dispose: () => {
      disposed = true
      if (timer !== undefined) clearTimeout(timer)
    },
  }
}

export interface SsoRequiredProbe extends SsoProbeState {
  onChange: (email: string) => void
  onBlur: (email: string) => void
}

export function useSsoRequiredProbe({
  check,
  debounceMs,
  label,
}: {
  check?: (email: string) => Promise<boolean>
  debounceMs?: number
  label?: string
}): SsoRequiredProbe {
  const [state, setState] = useState<SsoProbeState>({ ssoRequired: false, pending: false })
  // The machine outlives a render and owns a timer, so it is created once. `check` is read
  // through the closure the machine captured; every caller passes a stable function (a
  // module-level one, or `useEmailValidProbe`'s memoised wrapper).
  const machine = useMemo(
    () =>
      createSsoRequiredProbe({
        ...(check === undefined ? {} : { check }),
        ...(debounceMs === undefined ? {} : { debounceMs }),
        ...(label === undefined ? {} : { label }),
        onState: setState,
      }),
    [check, debounceMs, label],
  )
  useEffect(() => () => {
    machine.dispose()
  }, [machine])

  return { ...state, onChange: machine.change, onBlur: machine.blur }
}

/**
 * The extension popup's `email-validator` (whitelabel-ext `email-validator.component.ts`): asks
 * the server whether the address is acceptable, on the same machine as the SSO probe — flagged
 * only on a falsy answer (`if (!emailValid) throw`), and failing OPEN on a rejection, because a
 * failed probe says nothing about the address. careero's own screens carry an `email-validator`
 * attribute too, but careero defines no such directive, so there it does nothing — which is why
 * only the popup passes `checkEmailValid`.
 */
export function useEmailValidProbe(
  checkEmailValid: ((email: string) => Promise<boolean>) | undefined,
  debounceMs?: number,
): SsoRequiredProbe {
  const check = useMemo(
    () => (checkEmailValid === undefined ? undefined : async (email: string) => !(await checkEmailValid(email))),
    [checkEmailValid],
  )
  return useSsoRequiredProbe({
    ...(check === undefined ? {} : { check }),
    ...(debounceMs === undefined ? {} : { debounceMs }),
    label: 'isValidEmail',
  })
}
